import { useState } from 'react';
import {
  FileInput,
  Button,
  Group,
  Alert,
  Paper,
  Text,
  Accordion,
  ActionIcon,
  Tooltip,
  List,
  Code,
  Modal,
  Stack,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconUpload,
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
  IconCalendar,
  IconFile,
  IconEye,
} from '@tabler/icons-react';

interface ImportedEventData {
  name?: string;
  description?: string;
  event_start_date?: string;
  event_start_time?: string;
  event_end_date?: string;
  event_end_time?: string;
  address?: string;
  facebook_url?: string;
  facebook_event_id?: string;
  spans_next_day?: boolean;
  timezone?: string;
  detected_artist_names?: string[];
  detected_dance_style_names?: string[];
  detected_event_type_names?: string[];
}

interface FacebookEventImporterProps {
  onEventImported: (eventData: ImportedEventData) => void;
  disabled?: boolean;
}

const FacebookEventImporter: React.FC<FacebookEventImporterProps> = ({ onEventImported, disabled = false }) => {
  const [icsFile, setIcsFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tooltipOpened, setTooltipOpened] = useState(false);
  const [importModalOpened, setImportModalOpened] = useState(false);
  const [importedEventData, setImportedEventData] = useState<ImportedEventData | null>(null);

  const formatDateTime = (date?: string, time?: string) => {
    if (!date && !time) return '';

    try {
      // If we have both date and time, combine them
      if (date && time) {
        const dateTimeString = `${date} ${time}`;
        const dateObj = new Date(dateTimeString);

        if (!isNaN(dateObj.getTime())) {
          return dateObj.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
        }
      }

      // If only date, format without time
      if (date) {
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          return dateObj.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
        }
      }

      // Fallback to original format if parsing fails
      return [date, time].filter(Boolean).join(' ');
    } catch (error) {
      // Fallback to original format if any error occurs
      return [date, time].filter(Boolean).join(' ');
    }
  };

  const renderEventDetails = (ev: ImportedEventData) => (
    <Stack gap="sm" mt={12} style={{ textAlign: 'left', lineHeight: 1.5 }}>
      {ev.name && (
        <div>
          <strong>Name:</strong> {ev.name}
        </div>
      )}

      {(ev.event_start_date || ev.event_start_time) && (
        <div>
          <strong>Start:</strong> {formatDateTime(ev.event_start_date, ev.event_start_time)}
        </div>
      )}
      {(ev.event_end_date || ev.event_end_time) && (
        <div>
          <strong>End:</strong> {formatDateTime(ev.event_end_date, ev.event_end_time)}
        </div>
      )}
      {ev.address && (
        <div>
          <strong>Address:</strong> {ev.address}
        </div>
      )}

      {ev.detected_artist_names && ev.detected_artist_names.length > 0 && (
        <div>
          <strong>Artists:</strong> {ev.detected_artist_names.join(', ')}
        </div>
      )}
      {ev.detected_dance_style_names && ev.detected_dance_style_names.length > 0 && (
        <div>
          <strong>Dance Styles:</strong> {ev.detected_dance_style_names.join(', ')}
        </div>
      )}
      {ev.detected_event_type_names && ev.detected_event_type_names.length > 0 && (
        <div>
          <strong>Event Types:</strong> {ev.detected_event_type_names.join(', ')}
        </div>
      )}
    </Stack>
  );

  const handleImport = async () => {
    if (!icsFile) {
      setError('Please select an ICS file to import');
      return;
    }

    if (!icsFile.name.toLowerCase().endsWith('.ics')) {
      setError('Please select a valid .ics calendar file');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }

      // Create FormData to send the file
      const formData = new FormData();
      formData.append('ics_file', icsFile);

      const response = await fetch('/events/import_from_ics', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        const ev: ImportedEventData = data.event_data;

        // Store the imported data for the modal
        setImportedEventData(ev);

        // Show success notification with button to view details
        const notificationId = showNotification({
          title: 'Import Successful',
          message: (
            <Group gap="sm" align="center">
              <Text size="sm">Event imported successfully!</Text>
              <Button
                size="xs"
                variant="filled"
                color="green"
                leftSection={<IconEye size={14} />}
                onClick={() => setImportModalOpened(true)}
              >
                See Import Details
              </Button>
            </Group>
          ),
          color: 'green',
          icon: <IconCheck />,
          autoClose: 10000,
          withCloseButton: true,
        });

        // Pass the imported data to the parent component
        onEventImported(ev);

        // Clear the file input
        setIcsFile(null);
        setError('');
      } else {
        setError(data.error || 'Failed to import calendar file');
        showNotification({
          title: 'Import Failed',
          message: data.error || 'Could not import the calendar file',
          color: 'red',
          icon: <IconAlertCircle />,
          autoClose: 7000,
        });
      }
    } catch (err: any) {
      console.error('Error importing calendar file:', err);
      const errorMessage = 'Network error - please check your connection and try again';
      setError(errorMessage);
      showNotification({
        title: 'Import Error',
        message: errorMessage,
        color: 'red',
        icon: <IconAlertCircle />,
        autoClose: 7000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Accordion multiple className="form-accordion">
        <Accordion.Item value="calendar-import">
          <Accordion.Control>
            <Group gap="xs">
              <IconCalendar size={28} color="#4CAF50" />
              <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Import from Calendar File</Text>
              <Tooltip
                label="Upload an .ics calendar file from Facebook, Eventbrite, Google Calendar, or any other calendar platform to automatically fill in event details."
                multiline
                w={280}
                withArrow
                position="bottom"
                color="gray.9"
                transitionProps={{ duration: 200 }}
                opened={tooltipOpened}
              >
                <ActionIcon
                  variant="transparent"
                  size="sm"
                  onClick={() => setTooltipOpened((prev) => !prev)}
                  style={{ cursor: 'pointer' }}
                >
                  <IconInfoCircle size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Paper p="md" style={{ backgroundColor: 'rgba(56, 58, 64, 0.8)', borderRadius: '8px' }}>
              <Text size="sm" c="dimmed" mb="md">
                <strong>How to get your calendar file:</strong>
              </Text>

              <List size="sm" spacing="xs" mb="lg" style={{ color: '#A0A0A0' }}>
                <List.Item>
                  <strong>Facebook:</strong> Go to your event → Click the three dots (•••) → Select "Export Event" →
                  Download the <Code>.ics</Code> file
                </List.Item>
                <List.Item>
                  <strong>Eventbrite:</strong> Go to your event → Click "Add to Calendar" → Select "Download .ics file"
                </List.Item>
                <List.Item>
                  <strong>Google Calendar:</strong> Open event → Click the three dots → Select "Download as iCal"
                </List.Item>
              </List>

              <Group gap="sm" align="flex-end">
                <FileInput
                  placeholder="Choose .ics calendar file"
                  value={icsFile}
                  onChange={(file) => {
                    setIcsFile(file);
                    if (error) setError('');
                  }}
                  accept=".ics"
                  disabled={loading || disabled}
                  leftSection={<IconFile size={16} />}
                  style={{ flex: 1 }}
                  styles={{
                    input: {
                      backgroundColor: '#686868',
                      color: '#F2F2F2',
                      borderColor: error ? '#fa5252' : '#787878',
                    },
                  }}
                  error={error}
                />
                <Button
                  onClick={handleImport}
                  loading={loading}
                  disabled={!icsFile || disabled}
                  leftSection={<IconUpload size={16} />}
                  color="green"
                  className="clear-button"
                  variant="filled"
                >
                  {loading ? 'Importing...' : 'Import Event'}
                </Button>
              </Group>

              {error && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" variant="filled" mt="sm">
                  {error}
                </Alert>
              )}

              <Text size="xs" c="dimmed" mt="sm">
                <strong>Supported:</strong> .ics files from Facebook, Eventbrite, Google Calendar, Outlook, and more
              </Text>
            </Paper>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Modal
        opened={importModalOpened}
        onClose={() => setImportModalOpened(false)}
        title="Event Import Summary"
        size="md"
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={true}
        styles={{
          title: {
            fontSize: '1.25rem',
            fontWeight: 'bold',
          },
        }}
      >
        {importedEventData && (
          <>
            {renderEventDetails(importedEventData)}
            <Group justify="flex-end" mt="lg">
              <Button onClick={() => setImportModalOpened(false)} variant="filled" color="gray">
                Close
              </Button>
            </Group>
          </>
        )}
      </Modal>
    </>
  );
};

export default FacebookEventImporter;
